import { bound } from "@frui.ts/helpers";
import { ManualEntityValidator } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import { ExemptionState } from "models/orderItem";
import ValidChargeResponse from "models/validChargeResponse";
import Vehicle from "models/vehicle";
import VehicleChangeDuringValidityRequest from "models/vehicleChangeDuringValidityRequest";
import AppRouter from "services/appRouter";
import ChargeManagementService from "services/chargeManagementService";
import ChargeRegistrationService, { RequestSource } from "services/chargeRegistrationService";
import EventBus, {
  emailUpdated,
  putChargeContactFailure,
  putChargeContactSuccess,
  putChargeVehicleFailure,
  putChargeVehicleSuccess,
  vehicleHasValidCharges,
  vehicleRegistration,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import { EntityValidationRules } from "services/validationService";
import VehicleInfoViewModel from "../../common/vehicleInfoViewModel";
import exemptionKeys from "../../utils/exemptionKeys";
import ChargeManagementEditPartViewModel from "./chargeManagementEditPartViewModel";
import EmailInputViewModel from "./common/emailInputViewModel";
import VehicleRegistrationResponse from "entities/vehicleRegistrationResponse";
import { scrollToFirstError } from "utils/scrollTo";
import ChargeOrderInstructionsResponse from "entities/chargeOrderInstructionsResponse";
import EnumService from "services/enumService";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import EshopError from "models/eshopError";
import ChargeOverlapType from "entities/chargeOverlapType";
import ChargeVehicleWarningReasonOrder from "entities/chargeVehicleWarningReasonOrder";
import ChargeTypesService from "services/chargeTypesService";
import TimeService from "services/timeService";
import { dateStringWithServerZone, isBeforePragueDay } from "utils/dateUtils";
import { utcToCZZonedTime, dateToIsoDate } from "../../../utils/dateUtils";

export default abstract class ChargeManagementLicensePlateChangeViewModel extends ChargeManagementEditPartViewModel {
  abstract get validationRules(): EntityValidationRules<any>;
  abstract get newVehicle(): Vehicle | VehicleChangeDuringValidityRequest | VehicleRegistrationResponse | undefined;
  protected abstract putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>): Promise<void>;
  protected abstract putChargeVehicleSuccess(data: ReturnType<typeof putChargeVehicleSuccess>): Promise<void>;
  abstract submit(): Promise<void> | void;
  abstract onCancelSecondStep(): void;

  private untilRegex = /%until%/g;
  private sinceRegex = /%since%/g;
  protected manualValidator = new ManualEntityValidator<Vehicle>(false);
  @observable protected _validCharges?: ValidChargeResponse[];
  @observable protected _exemptionState?: ExemptionState;
  @observable protected _licensePlateNumberNotInSystem?: string;
  @observable protected _licensePlateCountryIdNotInSystem?: string;
  @observable originalVehicleInfoViewModel?: VehicleInfoViewModel;
  @observable newVehicleInfoViewModel: VehicleInfoViewModel;
  @observable emailInputViewModel?: EmailInputViewModel;
  @observable overlappingCharges?: any;

  @computed
  get exemptionInfo() {
    switch (this._exemptionState) {
      case ExemptionState.Exempted:
      case ExemptionState.C:
      case ExemptionState.O:
      case ExemptionState.P:
        const code = exemptionKeys(this._exemptionState);
        return code ? this.ta(code) : undefined;
      default:
        return undefined;
    }
  }

  @computed
  get validChargesInfo() {
    if (this._validCharges) {
      return this._validCharges
        .map(validCharge =>
          this.ta("validSinceRegistrationInfo")
            .replace(this.untilRegex, this.localizationService.formatDateTime(validCharge.validUntil))
            .replace(this.sinceRegex, this.localizationService.formatDateTime(validCharge.validSince))
        )
        .join("\r\n\r\n");
    }

    return undefined;
  }

  @computed
  get canEdit(): boolean {
    if (!this.charge || !this.charge.validSince) {
      return false;
    }
    const isBeforeValidity = this.timeService.currentServerTime <= utcToCZZonedTime(this.charge.validSince);
    if (isBeforeValidity) {
      return this.charge.permittedActions.vehicleChangeBeforeValidity.isAllowed;
    }
    return this.charge.permittedActions.vehicleChangeDuringValidity.isAllowed;
  }

  @computed
  get isSameVehicle() {
    return (
      this.charge?.vehicle.countryId === this.newVehicle?.countryId &&
      this.charge?.vehicle.licensePlate === this.newVehicle?.licensePlate
    );
  }

  constructor(
    public localizationService: LocalizationService,
    protected chargeManagementService: ChargeManagementService,
    protected timeService: TimeService,
    private chargeTypesService: ChargeTypesService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected signalRNotificationService: SignalRNotificationService,
    protected emailInputViewModelFactory: ReturnType<typeof EmailInputViewModel.Factory>,
    protected vehicleInfoViewModelFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    protected chargeRegistrationService: ChargeRegistrationService,
    protected router: AppRouter
  ) {
    super();
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(putChargeVehicleFailure, this.putChargeVehicleFailure),
      this.eventBus.subscribe(putChargeVehicleSuccess, this.putChargeVehicleSuccess),
      this.eventBus.subscribe(vehicleRegistration, this.vehicleRegistration),
      this.eventBus.subscribe(vehicleHasValidCharges, this.vehicleHasValidCharges),
      this.eventBus.subscribe(putChargeContactFailure, this.putChargeContactFailure),
      this.eventBus.subscribe(putChargeContactSuccess, this.putChargeContactSuccess),
      this.eventBus.subscribe(emailUpdated, this.onEmailUpdated)
    );

    this.originalVehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.charge?.vehicle!, {
      attachValidator: false,
      context: "old",
    });

    this.unsubscriptions.push(
      reaction(
        () => this.newVehicle?.countryId,
        () => {
          this.removeSameVehicleErrors();
          this.resetSecondStep();
        }
      ),
      reaction(
        () => this.newVehicle?.licensePlate,
        () => {
          this.removeSameVehicleErrors();
          this.resetSecondStep();
        }
      ),
      reaction(
        () => this.newVehicle?._countryIdConfirmation,
        () => {
          this.resetSecondStep();
        }
      ),
      reaction(
        () => this.newVehicle?._countryIdConfirmation,
        () => {
          this.resetSecondStep();
        }
      ),
      reaction(
        () => this.newVehicle?._licensePlateConfirmation,
        () => {
          this.resetSecondStep();
        }
      )
    );

    return super.onInitialize();
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @bound
  protected removeSameVehicleErrors() {
    this.manualValidator.removeError("licensePlate");
    this.manualValidator.removeError("countryId");
  }

  @bound
  protected sameVehicleCheck() {
    this.removeSameVehicleErrors();

    if (this.isSameVehicle) {
      const errorMessage = this.translate("validators.sameVehicle");
      this.manualValidator.addError("licensePlate", errorMessage);
      this.manualValidator.addError("countryId", errorMessage);
    }
  }

  @bound
  protected async checkRsvValidity(): Promise<boolean> {
    this.sameVehicleCheck();
    this.success = false;
    this.errorMessage = undefined;

    if (!this.validate()) {
      scrollToFirstError(this.formElement);
      return false;
    }

    const newCountryId = this.newVehicle?.countryId;
    const newLicensePlate = this.newVehicle?.licensePlate;

    if (!newCountryId || !newLicensePlate) return false;

    this.secondStepLoading = true;
    const [chargeOrderInstructionsResponse, chargeValidityInstructionsResponse] = await Promise.all([
      await this.chargeManagementService.getChargeOrderInstructions(newCountryId, newLicensePlate),
      await this.chargeManagementService.getChargeValidityInstructions(newCountryId, newLicensePlate, undefined, this.charge!.id),
      // Must be loaded here, otherwise it will not work in the translateWarningReason.
      await this.enumService.lazyValues("exemption_reasons"),
    ]);
    this.secondStepLoading = false;

    this.warningMessages = [];
    this.secondStepErrorMessage = await this.getSecondStepErrorMessage(chargeOrderInstructionsResponse);
    if (chargeOrderInstructionsResponse instanceof ChargeOrderInstructionsResponse) {
      const newMessages = chargeOrderInstructionsResponse.warningReasons
        ?.sort((a, b) => {
          return (
            ChargeVehicleWarningReasonOrder[a as keyof ChargeVehicleWarningReasonOrder] -
            ChargeVehicleWarningReasonOrder[b as keyof ChargeVehicleWarningReasonOrder]
          );
        })
        .map(reason => this.translateWarningReason(reason, chargeOrderInstructionsResponse.possibleExemptionReasonIds));
      if (newMessages) {
        this.warningMessages = [...this.warningMessages, ...newMessages];
      }
    }
    if (chargeValidityInstructionsResponse instanceof ChargeValidityInstructionsResponse) {
      this.overlappingCharges =
        chargeValidityInstructionsResponse.overlappingCharges?.map(overlap =>
          this.translateOverlappingCharge(overlap, this.isDifferentTimeZone ?? false)
        ) ?? [];
      this.warningMessages = [...this.warningMessages];

      if (chargeValidityInstructionsResponse.overlapTypes?.includes(ChargeOverlapType.ExemptionOverlap)) {
        const exemptionOverlapMessage = this.translateOverlapReason(ChargeOverlapType.ExemptionOverlap);
        this.warningMessages = [exemptionOverlapMessage, ...this.warningMessages];
      }
    }

    const isAnyFaulted =
      chargeOrderInstructionsResponse instanceof EshopError || chargeValidityInstructionsResponse instanceof EshopError;
    if (isAnyFaulted) {
      return false;
    }

    const isSuccessful =
      this.warningMessages.length === 0 && this.secondStepErrorMessage === undefined && this.overlappingCharges?.length === 0;
    if (isSuccessful) {
      this.hideConfirmChangeButton = true;
    } else {
      this.showSecondStep = true;
    }
    return isSuccessful;
  }

  @bound
  protected onEmailUpdated(data: ReturnType<typeof emailUpdated>) {
    this.setEmailAddress(data.payload.emailAddress);
    if (this.emailInputViewModel) {
      this.emailInputViewModel = undefined;
    }
  }

  @action.bound
  private vehicleHasValidCharges(data: ReturnType<typeof vehicleHasValidCharges>) {
    if (!data.payload.source || !data.payload.validCharges.charges.length) {
      this._validCharges = undefined;
    }

    if (data.payload.source === RequestSource.NewLicensePlate && data.payload.validCharges.charges.length) {
      this._validCharges = data.payload.validCharges.charges;
    }
  }

  @action.bound
  vehicleRegistration(data: ReturnType<typeof vehicleRegistration>) {
    const payload = data.payload;

    if (data.payload.source === RequestSource.NewLicensePlate) {
      if (payload.vehicleRegistration.isGivenExemption) {
        this._exemptionState = ExemptionState.Exempted;
      } else if (payload.vehicleRegistration.possibleExemption) {
        this._exemptionState = payload.vehicleRegistration.possibleExemption;
      }
    }
  }

  @bound
  protected putChargeContactFailure(data: ReturnType<typeof putChargeContactFailure>) {
    if (data.payload.triggerer !== "licensePlateChange") {
      return;
    }

    this.clearAfterAsyncAction();
    this.setErrorMessageBasedOnType(data.payload.type);
  }

  @bound
  protected putChargeVehicleFailure(data: ReturnType<typeof putChargeVehicleFailure>) {
    this.clearAfterAsyncAction();
    this.setErrorMessageBasedOnType(data.payload.type);
  }

  private async getSecondStepErrorMessage(chargeOrderInstructionsResponse: ChargeOrderInstructionsResponse | EshopError) {
    if (chargeOrderInstructionsResponse instanceof EshopError || !this.charge) {
      return undefined;
    }

    const fuelTypeLimitation = chargeOrderInstructionsResponse.fuelTypeLimitation;
    if (!fuelTypeLimitation) {
      return undefined;
    }

    await this.enumService.lazyValues("fuel_types");

    // If the current charge fuel type is the same as the RSV one, it should not show an error.
    const priceListItem = this.chargeTypesService?.getPriceItemList(true);
    const fuelTypeId = priceListItem?.find(x => x.id === this.charge?.priceListItemId)?.fuelTypeId;
    if (fuelTypeLimitation.allowedFuelTypeId === fuelTypeId) {
      return undefined;
    }

    const matchingFuelType = this.enumService.values("fuel_types").find(x => x.id === fuelTypeLimitation.allowedFuelTypeId);
    const errorMessage = this.localizationService
      .translateAttribute("fuelTypeLimitation", fuelTypeLimitation.limitationReason, undefined, true) // The 'true' is important for markdown links to function correctly.
      .replace("%FUEL_TYPE%", matchingFuelType?.name ?? "unknown");

    return errorMessage;
  }

  @bound
  private ta(code: string) {
    return this.localizationService.translateAttribute("orderItem", code);
  }
}
